import { useCallback, useEffect, useState } from "react";
import api, { rupiah, getErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Layers } from "lucide-react";

const COVERAGE_STYLE = {
  COMPLETE: "bg-green-100 text-green-800 border-green-300",
  INCOMPLETE: "bg-red-100 text-red-800 border-red-300",
  OVERLAP: "bg-amber-100 text-amber-800 border-amber-300",
  OUT_OF_PERIOD: "bg-amber-100 text-amber-800 border-amber-300",
};

// One logical population, however many physical files and storage chunks it is made of.
export default function LogicalPopulation({ id, datasetType = "GL" }) {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState("");
  const [view, setView] = useState(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/engagements/${id}/logical-datasets`)
      .then(({ data }) => {
        const rows = data.filter((row) => row.dataset_type === datasetType);
        setItems(rows);
        // With a single population there is nothing to choose; show it.
        setSelected((current) => current || (rows.length === 1 ? rows[0].id : ""));
      })
      .catch(() => {});
  }, [id, datasetType]);
  useEffect(() => { setSelected(""); setView(null); load(); }, [load]);

  useEffect(() => {
    if (!selected) { setView(null); return; }
    api.get(`/engagements/${id}/logical-datasets/${selected}`)
      .then(({ data }) => setView(data))
      .catch((e) => toast.error(getErrorMessage(e)));
  }, [id, selected]);

  const create = async () => {
    if (!name.trim()) return toast.error("Beri nama populasi logis");
    setBusy(true);
    try {
      const { data } = await api.post(`/engagements/${id}/logical-datasets`, { name: name.trim(), dataset_type: datasetType });
      setName(""); load(); setSelected(data.id);
      toast.success("Populasi logis dibuat");
    } catch (e) { toast.error(getErrorMessage(e)); } finally { setBusy(false); }
  };

  const coverage = view?.coverage;
  return (
    <div className="bg-white border border-border rounded-sm p-4 space-y-3" data-testid="logical-population">
      <div className="flex items-center gap-2 font-head font-semibold text-sm"><Layers size={15} />Populasi Logis {datasetType}</div>
      <div className="flex flex-wrap items-end gap-3">
        <div><Label className="text-xs">Populasi</Label>
          <Select value={selected} onValueChange={setSelected}>
            <SelectTrigger className="h-9 w-72" data-testid="lp-select"><SelectValue placeholder="Pilih populasi logis" /></SelectTrigger>
            <SelectContent>{items.map((row) => <SelectItem key={row.id} value={row.id}>{row.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs">Populasi baru</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="General Ledger — Jan–Jun 2026" className="h-9 w-72" data-testid="lp-name" /></div>
        <Button onClick={create} disabled={busy} data-testid="lp-create">Buat</Button>
      </div>

      {view && (
        <div className="space-y-3 text-xs" data-testid="lp-view">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <Figure label="Periode audit" value={view.audit_period_display} />
            <Figure label="Sumber" value={`${view.totals.source_count} berkas`} />
            <Figure label="Baris" value={Number(view.totals.row_count).toLocaleString("id-ID")} />
            <Figure label="Cakupan" value={coverage ? `${coverage.covered} bulan` : "—"} />
            <Figure label="Debit" value={rupiah(view.totals.debit_exact)} />
            <Figure label="Kredit" value={rupiah(view.totals.credit_exact)} />
            <Figure label="Neto" value={rupiah(view.totals.net_exact)} />
            <div className="border border-border rounded-sm p-2">
              <div className="text-muted-foreground">Status</div>
              <div className={`inline-block mt-1 px-2 py-0.5 border rounded-sm ${COVERAGE_STYLE[coverage?.status] || "bg-slate-100 text-slate-600 border-slate-300"}`} data-testid="lp-status">
                {view.status}{coverage ? ` · ${coverage.status}` : ""}
              </div>
            </div>
          </div>

          {!!view.blockers?.length && (
            <ul className="border border-amber-300 bg-amber-50 rounded-sm p-2 space-y-1" data-testid="lp-blockers">
              {view.blockers.map((blocker) => <li key={blocker}>• {blocker}</li>)}
            </ul>
          )}

          <div className="overflow-x-auto">
            <table className="w-full audit-table" data-testid="lp-sources">
              <thead><tr>
                <th>Berkas</th><th>Segmen</th><th>Cakupan</th><th className="text-right">Baris</th>
                <th className="text-right">Debit</th><th className="text-right">Kredit</th><th className="text-right">Neto</th><th>Penyimpanan</th>
              </tr></thead>
              <tbody>
                {view.sources.map((source) => (
                  <tr key={source.dataset_id} data-testid={`lp-source-${source.dataset_id}`}>
                    <td>{source.source_filename}</td>
                    <td>{source.source_segment || "—"}</td>
                    <td>{Object.keys(source.months || {}).sort().join(", ") || "—"}</td>
                    <td className="text-right tabular-nums">{Number(source.rows).toLocaleString("id-ID")}</td>
                    <td className="text-right tabular-nums">{rupiah(source.debit_exact)}</td>
                    <td className="text-right tabular-nums">{rupiah(source.credit_exact)}</td>
                    <td className="text-right tabular-nums">{rupiah(source.net_exact)}</td>
                    <td>{source.storage === "CHUNKED" ? `${source.chunk_count} bongkahan` : "inline"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {coverage && (
            <div className="flex flex-wrap gap-1" data-testid="lp-months">
              {coverage.expected_months.map((month) => {
                const rows = coverage.rows_by_month[month];
                const missing = coverage.missing_months.includes(month);
                const overlap = Object.keys(coverage.overlapping_months || {}).includes(month);
                const style = missing ? "bg-red-100 text-red-800 border-red-300"
                  : overlap ? "bg-amber-100 text-amber-800 border-amber-300"
                  : "bg-green-100 text-green-800 border-green-300";
                return (
                  <span key={month} className={`px-2 py-0.5 border rounded-sm ${style}`} title={missing ? "Tidak ada sumber" : `${rows} baris`}>
                    {month}{missing ? " — kosong" : overlap ? " — tumpang tindih" : ` · ${Number(rows).toLocaleString("id-ID")}`}
                  </span>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Figure({ label, value }) {
  return (
    <div className="border border-border rounded-sm p-2">
      <div className="text-muted-foreground">{label}</div>
      <div className="font-medium tabular-nums">{value}</div>
    </div>
  );
}
